import { isTeammateATeamMember } from "@repo/app-types/entities/teammate";
import { isNotNullNotUndefined } from "@repo/app-types/helpers/utilities";
import {
  IconBriefcase,
  IconChartBar,
  IconSchool,
  IconUserBolt,
  type IconProps,
} from "@tabler/icons-react";
import type { ComponentType, RefAttributes } from "react";
import z from "zod";
import { selectTeammate } from "../../features/teammate/teammateSlice.js";
import { useMainNavigationFeatureFlags } from "../../helpers/mainNavigation.js";
import { useAppSelector } from "../../hooks/hook.js";

export const WorkspaceId = {
  Recruiting: "recruiting",
  Performance: "performance",
  Agents: "agents",
  Training: "training",
} as const;

const allWorkspaceIds = [
  WorkspaceId.Recruiting,
  WorkspaceId.Performance,
  WorkspaceId.Agents,
  WorkspaceId.Training,
];

export const workspaceIdSchema = z.enum(allWorkspaceIds);

export type WorkspaceId = z.infer<typeof workspaceIdSchema>;

export interface WorkspaceOption {
  id: WorkspaceId;
  label: string;
  Icon: ComponentType<IconProps & RefAttributes<SVGSVGElement>>;
  href: string;
}

type MainNavigationFeatureFlags = ReturnType<
  typeof useMainNavigationFeatureFlags
>;

// The workspace the `/` route (HomePage) redirects to, based on feature flags.
// The nav derives its own default from `useWorkspaceOptions` instead.
export function determineDefaultWorkspaceId(
  flags: MainNavigationFeatureFlags,
): WorkspaceId {
  if (flags.hasRecruiting) return WorkspaceId.Recruiting;
  if (flags.hasTeamPerformanceTab) return WorkspaceId.Performance;
  if (flags.hasTraining) return WorkspaceId.Training;
  return WorkspaceId.Recruiting;
}

export function useWorkspaceOptions(): readonly WorkspaceOption[] {
  const teammate = useAppSelector(selectTeammate);

  const { hasRecruiting, hasTraining, hasTeamPerformanceTab, hasAgentsTab } =
    useMainNavigationFeatureFlags();

  const isTeamMember = teammate && isTeammateATeamMember(teammate);

  const nonRecruitingOptions: readonly WorkspaceOption[] = [
    hasTeamPerformanceTab
      ? {
          id: WorkspaceId.Performance,
          label: "Performance",
          Icon: IconChartBar,
          href: "/performance/playbooks",
        }
      : undefined,
    hasAgentsTab && !isTeamMember
      ? {
          id: WorkspaceId.Agents,
          label: "Voice agents",
          Icon: IconUserBolt,
          href: "/agents",
        }
      : undefined,
    hasTraining && !isTeamMember
      ? {
          id: WorkspaceId.Training,
          label: "Training",
          Icon: IconSchool,
          href: "/train",
        }
      : undefined,
  ].filter(isNotNullNotUndefined);

  return [
    hasRecruiting && !isTeamMember
      ? {
          id: WorkspaceId.Recruiting,
          label: "Hire",
          Icon: IconBriefcase,
          href: "/",
        }
      : undefined,
    ...nonRecruitingOptions,
  ].filter(isNotNullNotUndefined);
}
